<script lang="ts">
import { defineComponent, computed } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { Partner, PartnerDisplayName } from '@/types'
import config from '@/config'

interface Props {
  title: string
  subTitle: string | null
  logoSrc: string | null
  logoAlt: string | null
}

const { useGetters: useLoginDomainStoreGetters } = createNamespacedHelpers('LoginDomain')

export default defineComponent<Props>({
  name: 'PageTitle',

  props: {
    title: {
      type: String,
      required: true,
    },
    subTitle: {
      type: String,
      required: false,
      default: null,
    },
    logoSrc: {
      type: String,
      required: false,
      default: null,
    },
    logoAlt: {
      type: String,
      required: false,
      default: null,
    },
  },

  setup(props: Props) {

    const { themeName, logo, themeCode } = useLoginDomainStoreGetters(['themeName', 'logo', 'themeCode'])

    const resolvedLogo = computed(() => {
      if (props.logoSrc) return props.logoSrc
      if (config?.USE_AUTHX_THEME_LOGO_URL === 'true') return logo.value
      return themeCode.value === Partner.LittleHotelier
        ? config?.LITTLE_HOTELIER_THEME_LOGO_URL
        : config?.SITEMINDER_THEME_LOGO_URL
    })

    const resolvedAlt = computed(() => {
      if (props.logoAlt) return props.logoAlt
      if (config?.USE_AUTHX_THEME_LOGO_URL === 'true') return themeName.value
      return themeCode.value === Partner.LittleHotelier
        ? PartnerDisplayName.LittleHotelier
        : PartnerDisplayName.SiteMinder
    })

    return {
      resolvedLogo,
      resolvedAlt,
    }

  },

})

</script>

<template>
  <header class="page_title">
    <div class="container">
      <figure v-if="resolvedLogo">
        <img
          :src="resolvedLogo"
          :alt="resolvedAlt"
        >
        <span class="brand-name">{{ resolvedAlt }}</span>
      </figure>

      <h3 v-if="title">
        {{ title }}
      </h3>
      <p v-if="subTitle">
        {{ subTitle }}
      </p>
    </div>
  </header>
</template>

<style lang="scss" scoped>
  .page_title {
    @apply text-center px-2.5;

    figure {
      @apply mb-8;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }

    img {
      @apply w-16;
    }

    h3 {
      @apply mb-2;
    }
  }

  .brand-name {
    display: none;
    color: #333;
    font-size: 28px;
    font-weight: 600;
    line-height: 22px;
    letter-spacing: -0.2px;

    @media (max-width: 639px) {
      display: block;
    }
  }
</style>
